import { useAnswersStore } from "@/store/answers";
import { useClientStore } from "@/store/client";
import { useEventsStore } from "@/store/events";
import { useEventTypesStore } from "@/store/eventTypes";
import { useIndicatorsStore } from "@/store/indicators";
import { useInstancesStore } from "@/store/instances";
import { ListStore } from "@/store/list";
import { useTeamsStore } from "@/store/teams";
import { useTemplatesStore } from "@/store/templates";
import { useTicketsStore } from "@/store/tickets";
import { useTicketTemplatesStore } from "@/store/ticketTemplates";
import {
  EventType,
  ExecutionAnswer,
  FabriqEvent,
  Field,
  Group,
  Indicator,
  ListEntity,
  RoutineInstance,
  Team,
  Template,
  Ticket,
  TicketColumn,
  TicketFields,
  TicketTemplate,
} from "@/types";
import { localLogger } from "@/utils/localLogger";

const propertyTypes = [
  "categories",
  "statuses",
  "labels",
  "equipments",
  "products",
  "priorities",
  "custom_1",
  "custom_2",
] as const;

type PropertyType = (typeof propertyTypes)[number];

export function useHandleSocketPropertiesMergedEvent() {
  const clientStore = useClientStore();
  const ticketsStore = useTicketsStore();
  const ticketTemplateStore = useTicketTemplatesStore();
  const teamsStore = useTeamsStore();
  const answerStore = useAnswersStore();
  const templateStore = useTemplatesStore();
  const indicatorStore = useIndicatorsStore();
  const instancesStore = useInstancesStore();
  const eventsStore = useEventsStore();
  const eventsTypesStore = useEventTypesStore();

  function handleSocketPropertiesMergedEvent(
    payload: [
      {
        property_type: PropertyType;
        source_ids: number[];
        destination_id: number;
      }
    ]
  ): void {
    const [args] = payload ?? [];
    const {
      property_type: propertyType,
      source_ids: sourceIds,
      destination_id: destinationId,
    } = args ?? [];
    if (!propertyType || !propertyTypes.includes(propertyType)) {
      localLogger.log(
        `Invalid payload for properties merged event: Invalid property_type`
      );
      return;
    }
    if (
      !Array.isArray(sourceIds) ||
      !sourceIds?.length ||
      sourceIds.some((id) => typeof id !== "number")
    ) {
      localLogger.log(
        `Invalid payload for properties merged event: Invalid source_ids`
      );
      return;
    }
    if (typeof destinationId !== "number") {
      localLogger.log(
        `Invalid payload for properties merged event: Invalid destination_id`
      );
      return;
    }
    switch (propertyType) {
      case "categories":
        return mergeCategories(sourceIds, destinationId);
      case "labels":
        return mergeLabels(sourceIds, destinationId);
      case "statuses":
        return mergeStatuses(sourceIds, destinationId);
      case "equipments":
        return mergeFields(sourceIds, destinationId, "equipment");
      case "products":
        return mergeFields(sourceIds, destinationId, "product");
      case "priorities":
        return mergeFields(sourceIds, destinationId, "priority");
      case "custom_1":
        return mergeFields(sourceIds, destinationId, "custom_1");
      case "custom_2":
        return mergeFields(sourceIds, destinationId, "custom_2");
      default:
        return;
    }
  }

  function mergeCategories(sourceIds: number[], destinationId: number): void {
    clientStore.setCategories(
      clientStore.categories.filter((c) => !sourceIds.includes(c.id))
    );

    teamsStore.collection.forEach((team: Team) =>
      mergePropertyInItem(
        team,
        "categories",
        sourceIds,
        destinationId,
        teamsStore
      )
    );

    ticketsStore.collection.forEach((ticket: Ticket) =>
      mergePropertyInItem(
        ticket,
        "categories",
        sourceIds,
        destinationId,
        ticketsStore
      )
    );

    answerStore.collection.forEach((answer: ExecutionAnswer) => {
      if (!answer.step) return;
      if (!hasToMergeProperty(answer.step, "category", sourceIds)) return;
      answerStore.update(
        answer.uuid,
        {
          step: {
            ...answer.step,
            category: destinationId,
          },
        },
        true
      );
    });

    templateStore.collection.forEach((template: Template) => {
      let changed = false;
      const steps = template.steps.map((step) => {
        if (!hasToMergeProperty(step, "category", sourceIds)) return step;
        changed = true;
        return {
          ...step,
          category: destinationId,
        };
      });
      if (!changed) return;
      templateStore.update(
        template.uuid,
        {
          steps,
        },
        true
      );
    });

    indicatorStore.collection.forEach((indicator: Indicator) => {
      if (!hasToMergeProperty(indicator, "category", sourceIds)) return;
      indicatorStore.update(
        indicator.uuid,
        {
          category: destinationId,
        },
        true
      );
    });

    ticketTemplateStore.collection.forEach((ticketTemplate: TicketTemplate) =>
      mergeTicketTemplateProperty(
        ticketTemplate,
        {
          type: "column",
          column: TicketColumn.Categories,
        },
        ticketTemplateStore,
        sourceIds,
        destinationId
      )
    );
  }

  function mergeLabels(sourceIds: number[], destinationId: number): void {
    clientStore.setLabels(
      clientStore.labels.filter((l) => !sourceIds.includes(l.id))
    );

    teamsStore.collection.forEach((team: Team) =>
      mergePropertyInItem(team, "labels", sourceIds, destinationId, teamsStore)
    );

    ticketsStore.collection.forEach((ticket: Ticket) =>
      mergePropertyInItem(
        ticket,
        "labels",
        sourceIds,
        destinationId,
        ticketsStore
      )
    );

    ticketTemplateStore.collection.forEach((ticketTemplate: TicketTemplate) =>
      mergeTicketTemplateProperty(
        ticketTemplate,
        {
          type: "column",
          column: TicketColumn.Labels,
        },
        ticketTemplateStore,
        sourceIds,
        destinationId
      )
    );
  }

  function mergeStatuses(sourceIds: number[], destinationId: number): void {
    clientStore.setStatuses(
      clientStore.statuses.filter((s) => !sourceIds.includes(s.id))
    );

    teamsStore.collection.forEach((team: Team) =>
      mergePropertyInItem(
        team,
        "ticket_statuses",
        sourceIds,
        destinationId,
        teamsStore
      )
    );

    ticketsStore.collection.forEach((ticket: Ticket) => {
      if (!hasToMergeProperty(ticket, "status", sourceIds)) return;
      ticketsStore.update(
        ticket.uuid,
        {
          status: destinationId,
        },
        true
      );
    });

    ticketTemplateStore.collection.forEach((ticketTemplate: TicketTemplate) =>
      mergeTicketTemplateProperty(
        ticketTemplate,
        {
          type: "column",
          column: TicketColumn.Status,
        },
        ticketTemplateStore,
        sourceIds,
        destinationId
      )
    );
  }

  function getDestinationFromGroups(destinationId: number): Field | undefined {
    return clientStore.groups
      .flatMap((group: Group) => group.extra_fields)
      .find((field: Field) => field.id === destinationId);
  }

  function mergeFields(
    sourceIds: number[],
    destinationId: number,
    key: keyof TicketFields
  ): void {
    clientStore.setExtraFields(
      clientStore.extra_fields.filter((f) => !sourceIds.includes(f.id))
    );

    const destination =
      clientStore.extra_fields.find((f) => f.id === destinationId) ??
      getDestinationFromGroups(destinationId);
    if (destination) {
      const groups = clientStore.groups.map((group: Group) => {
        const hasSource = group.extra_fields.some((f) =>
          sourceIds.includes(f.id)
        );
        if (!hasSource) return group;
        const extra_fields = group.extra_fields.filter(
          (f) => !sourceIds.includes(f.id)
        );
        extra_fields.push(destination as Field);
        return { ...group, extra_fields };
      });
      clientStore.setGroups(groups);
    }

    teamsStore.collection.forEach((team: Team) =>
      mergePropertyInItem(
        team,
        "extra_fields",
        sourceIds,
        destinationId,
        teamsStore
      )
    );

    ticketsStore.collection.forEach((ticket: Ticket) => {
      if (!ticket.extra_fields?.[key]) return;
      if (!hasToMergeProperty(ticket.extra_fields, key, sourceIds)) return;
      const values = ticket.extra_fields[key].filter(
        (id: number) => !sourceIds.includes(id)
      );
      if (!values.includes(destinationId)) {
        values.push(destinationId);
      }
      ticketsStore.update(
        ticket.uuid,
        {
          extra_fields: {
            ...ticket.extra_fields,
            [key]: values,
          },
        },
        true
      );
    });

    instancesStore.collection.forEach((instance: RoutineInstance) => {
      if (
        key !== "equipment" &&
        key !== "product" &&
        key !== "custom_1" &&
        key !== "custom_2"
      )
        return;
      if (!instance.scheduler?.properties?.[key]) return;
      if (!hasToMergeProperty(instance.scheduler.properties, key, sourceIds))
        return;
      const values = instance.scheduler.properties[key].filter(
        (id: number) => !sourceIds.includes(id)
      );
      if (!values.includes(destinationId)) {
        values.push(destinationId);
      }
      instancesStore.update(
        instance.uuid,
        {
          scheduler: {
            ...instance.scheduler,
            properties: {
              ...instance.scheduler.properties,
              [key]: values,
            },
          },
        },
        true
      );
    });

    ticketTemplateStore.collection.forEach((ticketTemplate: TicketTemplate) =>
      mergeTicketTemplateProperty(
        ticketTemplate,
        {
          type: "field",
          column: key,
        },
        ticketTemplateStore,
        sourceIds,
        destinationId
      )
    );

    const eventPropertyTypeIds: string[] = [];
    eventsTypesStore.collection.forEach((eventType: EventType) => {
      eventType.properties.forEach((property) => {
        if (property.type !== key) return;
        eventPropertyTypeIds.push(property.id);
      });
    });

    eventsStore.collection.forEach((event: FabriqEvent) => {
      let changed = false;
      const properties = event.properties.map((property) => {
        if (!eventPropertyTypeIds.includes(property.eventPropertyTypeId))
          return property;
        if (!sourceIds.includes(Number(property.value))) return property;
        changed = true;
        return {
          ...property,
          value: destinationId,
        };
      });
      if (!changed) return;
      eventsStore.update(
        event.uuid,
        {
          properties: properties,
        },
        true
      );
    });
  }

  return handleSocketPropertiesMergedEvent;
}

export type SocketPropertiesMergedEventHandler = (
  payload: [
    {
      property_type:
        | "categories"
        | "statuses"
        | "labels"
        | "equipments"
        | "products"
        | "priorities"
        | "custom_1"
        | "custom_2";
      source_ids: number[];
      destination_id: number;
    }
  ]
) => void;

function mergePropertyInItem<
  K extends keyof T,
  T extends ListEntity & { [P in K]?: number[] }
>(
  item: T,
  key: K,
  sourceIds: number[],
  destinationId: number,
  store: ListStore<T>
) {
  if (!item[key]?.length) return;
  if (!item[key].some((id) => sourceIds.includes(id))) return;
  const items = item[key].filter((id) => !sourceIds.includes(id));
  if (items.includes(destinationId)) {
    return store.update(
      item.uuid,
      {
        [key]: items,
      },
      true
    );
  }
  return store.update(
    item.uuid,
    {
      [key]: [...items, destinationId],
    },
    true
  );
}

function hasToMergeProperty<
  K extends keyof T,
  T extends { [P in K]?: number | number[] | null }
>(item: T, key: K, sourceIds: number[]) {
  const categoryId = item[key];
  if (Array.isArray(categoryId)) {
    if (!categoryId.length) return false;
    if (!categoryId.some((id) => sourceIds.includes(id))) return false;
  } else {
    if (typeof categoryId !== "number") return false;
    if (!sourceIds.includes(categoryId)) return false;
  }
  return true;
}

function mergeTicketTemplateProperty(
  template: TicketTemplate,
  options:
    | {
        type: "column";
        column: TicketColumn;
      }
    | {
        type: "field";
        column: keyof TicketFields;
      },
  store: ListStore<TicketTemplate>,
  sourceIds: number[],
  destinationId: number
): void {
  let changed = false;
  const { type, column } = options;
  const fields = template.fields.map((field) => {
    if (field.type !== type) return field;
    if (type === "column" && field.label !== column) return field;
    if (type === "field" && field.field_type !== column) return field;
    if (!hasToMergeProperty(field, "value", sourceIds)) return field;
    changed = true;
    const value = field.value.filter((id: number) => !sourceIds.includes(id));
    if (!value.includes(destinationId)) {
      value.push(destinationId);
    }
    return {
      ...field,
      value,
    };
  });
  if (!changed) return;
  store.update(
    template.uuid,
    {
      fields,
    },
    true
  );
}
